import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  Drawer,
  useMediaQuery,
  useTheme,
  IconButton,
  Fab,
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import {
  selectSearchQuery,
  selectSearchLoading,
  selectSearchError,
  setQuery,
} from '../features/search/searchSlice';
import SearchBar from '../components/search/SearchBar';
import SearchFilters, { SearchFiltersType } from '../components/search/SearchFilters';
import SearchResults from '../components/search/SearchResults';
import { SearchResult } from '../types';
import { useNavigate } from 'react-router-dom';
import { searchService } from '../services/search.service';

const DRAWER_WIDTH = 320;

const SearchPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const query = useAppSelector(selectSearchQuery);
  const isLoading = useAppSelector(selectSearchLoading);
  const error = useAppSelector(selectSearchError);

  const [filters, setFilters] = useState<SearchFiltersType>({
    categories: [],
    fileTypes: [],
    dateRange: { start: null, end: null },
    tags: [],
    uploadedBy: [],
    searchType: 'basic',
    sortBy: 'relevance',
    sortOrder: 'desc',
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchTime, setSearchTime] = useState<number>();
  const [filtersOpen, setFiltersOpen] = useState(!isMobile);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    // Reset page when query or filters change
    setPage(1);
  }, [query, filters]);

  const handleSearch = useCallback(async (
    searchQuery: string,
    searchFilters: SearchFiltersType,
    searchPage: number = page,
    searchPageSize: number = pageSize
  ) => {
    if (!searchQuery.trim()) return;

    const startTime = performance.now();

    try {
      // Call actual search API
      const response = await searchService.search({
        query: searchQuery,
        searchType: searchFilters.searchType,
        filters: {
          categories: searchFilters.categories,
          tags: searchFilters.tags,
          fileTypes: searchFilters.fileTypes,
          dateRange: searchFilters.dateRange.start || searchFilters.dateRange.end ? {
            start: searchFilters.dateRange.start?.toISOString(),
            end: searchFilters.dateRange.end?.toISOString(),
          } : undefined,
          uploadedBy: searchFilters.uploadedBy,
        },
        page: searchPage,
        pageSize: searchPageSize,
        sortBy: searchFilters.sortBy,
        sortOrder: searchFilters.sortOrder,
        includeFacets: true,
      });

      // Store search results in state (since we're not using Redux for this yet)
      setSearchResults(response.results);
      setTotalCount(response.total);

      const endTime = performance.now();
      setSearchTime((endTime - startTime) / 1000);

      // Track search event for analytics
      await searchService.trackSearch({
        query: searchQuery,
        searchType: searchFilters.searchType,
        resultsCount: response.total,
        filters: {
          categories: searchFilters.categories,
          tags: searchFilters.tags,
          fileTypes: searchFilters.fileTypes,
          uploadedBy: searchFilters.uploadedBy,
        },
      });

    } catch (err) {
      console.error('Search failed:', err);
      // In production, show error to user
      setSearchResults([]);
      setTotalCount(0);
    }
  }, [page, pageSize]);

  useEffect(() => {
    if (query) {
      handleSearch(query, filters, 1, pageSize);
    }
  }, [query, filters, page, pageSize, handleSearch]);

  const handleFiltersChange = (newFilters: SearchFiltersType) => {
    setFilters(newFilters);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(`/documents/${result.id}`);
  };

  const toggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  };

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link color="inherit" href="/dashboard" underline="hover">
          {t('navigation.dashboard')}
        </Link>
        <Typography color="text.primary">{t('navigation.search')}</Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Typography variant="h4" gutterBottom>
        {t('search.searchDocuments')}
      </Typography>

      {/* Search Bar */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <SearchBar
          onSearch={(searchQuery) => {
            dispatch(setQuery(searchQuery));
            handleSearch(searchQuery, filters);
          }}
          placeholder={t('search.searchPlaceholder')}
        />
      </Paper>

      {/* Main Content */}
      <Box display="flex" gap={3}>
        {/* Filters Sidebar - Desktop */}
        {!isMobile && (
          <Box width={DRAWER_WIDTH} flexShrink={0}>
            <SearchFilters
              onFiltersChange={handleFiltersChange}
              initialFilters={filters}
            />
          </Box>
        )}

        {/* Results Area */}
        <Box flex={1} minWidth={0}>
          <SearchResults
            results={searchResults}
            isLoading={isLoading}
            error={error}
            totalCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onResultClick={handleResultClick}
            searchTime={searchTime}
            query={query}
          />
        </Box>
      </Box>

      {/* Mobile Filters Drawer */}
      {isMobile && (
        <>
          <Fab
            color="primary"
            size="medium"
            onClick={toggleFilters}
            sx={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1000,
            }}
          >
            <FilterListIcon />
          </Fab>

          <Drawer
            anchor="right"
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            PaperProps={{
              sx: { width: DRAWER_WIDTH },
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
              <Typography variant="h6">{t('search.filters')}</Typography>
              <IconButton onClick={() => setFiltersOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box px={2}>
              <SearchFilters
                onFiltersChange={(newFilters) => {
                  handleFiltersChange(newFilters);
                  if (isMobile) setFiltersOpen(false);
                }}
                initialFilters={filters}
              />
            </Box>
          </Drawer>
        </>
      )}
    </Box>
  );
};

export default SearchPage;
